/*
 * interfaces for logging and instrumentation data
 */

import { EventFlags, IActivityScope } from '@microsoft/oteljs'

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export type RecursiveIndex<T> = { [key: string]: RecursiveIndex<T> } | T

// loglevel for log statements
export enum LogLevel {
  None = 0,
  Trace = 1,
  Info = 2,
  Warning = 3,
  Error = 4,
}

// event result
export type ResultType =
  | 'None'
  | 'Success'
  | 'Failure'
  | 'Timeout'
  | 'Disallowed'
  | 'Cancelled'

// event action data/payload
export interface IEventPayloadData {
  // actionSource depends on ActionType
  actionSource?: string
  actionTarget?: string

  // text length, num words etc
  actionDataLength?: number
  actionIntData?: number

  resultType?: ResultType
  resultInfo?: string

  responseCode?: number
  responseTime?: number
  responseLength?: number
}

export interface IEventActionData extends IEventPayloadData {
  eventTagId: number
  eventActionType: string
}

// sink specific context
export interface IOTelActivityContext {
  activityScope: IActivityScope
  aggregatedScope?: IActivityScope
}

export interface ISinkContext {
  context: IOTelActivityContext
}

// event action for begin/end logging
export interface IBeginEventData {
  eventActionType: string
  eventStartTime: number
  eventData: EventData
  eventEnded: boolean
  eventTagId: number
  sinkContexts: { [index: string]: ISinkContext }
}

// browser/window data
export interface IBrowserData {
  // browser Navigator object
  browserLang: string
  browserOnline: boolean
  browserUserAgent: string

  // screen object
  screenHeight: number
  screenWidth: number
  colorDepth: number
  pixelDepth: number
}

// core log meta data
export interface IMetadata {
  correlationId: string
  currentTime: string
  environment: string
  sessionId: string
  sessionStartTime: string
}

// log message data
export interface IMessageData {
  eventTagId: number
  logLevel: LogLevel
  message: string
  messageDetails?: string[]
}

export type MessageData = IMessageData & { eventFlags?: Partial<EventFlags> }

// higher level log entry object that holds all others
export type CustomLogDataValueType = string | number | boolean
export type CustomLogData = {
  [key: string]: CustomLogDataValueType | undefined
}

export interface ILogEntry {
  browserData?: IBrowserData
  customData?: CustomLogData
  eventActionData?: IEventActionData
  eventFlags: EventFlags
  messageData?: IMessageData
  metadata: IMetadata
}

export interface ILogSink {
  // name of the sink. usually class SinkClass.name
  name(): string
  isLocal?(): boolean

  // push the logentry to sink endpoint
  writeEntry(logEntry: ILogEntry): void

  // Event scope methods
  beginEvent(
    eventActionType: string,
    eventFlags: EventFlags
  ): ISinkContext | null

  endEvent(logEntry: ILogEntry, context: ISinkContext): void

  // flush any entries in queue
  flush?(): void
}

export type EventData = {
  customData?: CustomLogData
  eventFlags?: Partial<EventFlags>
  payloadData?: IEventPayloadData
}

export type MessageOptions =
  | string
  | { eventFlags?: Partial<EventFlags>; message: string }

export type InstrumentOptions = EventData & {
  logError?: boolean
  rethrow?: boolean
  onEndEvent?(resultType?: ResultType): void
}
